import ArrowBackLight from "@assets/navigation/back.svg";
import LightCustomButton from "@components/buttons/LightCustomButton";
import DataPill from "@components/pills/DataPill";
import { institutionApis } from "@services/apis/Institution";
import { GetInstitutionalAccountDataResponse } from "@services/models/Institution/GetInstitutionalAccountDataResponse";
import { GetInstitutionalUsersData } from "@services/models/Institution/GetInstiutionalUsersModel";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";
import AddInstitutionServiceSlider from "../../instituitional-management/components/AddInstitutionServiceSlider";
import MerchantConfigPanel from "../../instituitional-management/components/MerchantConfigPanel";

const MobileInstitutionalManagementDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const heading = location.state?.heading || "Institution Details";

  const [institutionData, setInstitutionData] =
    useState<GetInstitutionalUsersData>();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [addServiceOpen, setAddServiceOpen] = useState(false);
  const [selectedAccountType, setSelectedAccountType] = useState<
    number | undefined
  >(undefined);
  const [grantingAccess, setGrantingAccess] = useState<boolean>(false);
  const { handleSuccess, handleError } = useSnackbarMessage();

  const fetchInstitutionData = async () => {
    try {
      setLoading(true);
      const response: GetInstitutionalAccountDataResponse =
        await institutionApis.getInstituionalAccountDetails({ userId: id! });
      setInstitutionData(response.data);
    } catch (err: any) {
      setError("Failed to fetch institutional users data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstitutionData();
  }, []);

  const handleGrantAnalyticsDashboardAccess = async () => {
    if (!institutionData?.userId) {
      handleError({ message: "User ID not found" });
      return;
    }

    try {
      setGrantingAccess(true);
      await institutionApis.grantAnalyticsDashboardAccess({
        userId: Number(institutionData.userId),
      });
      handleSuccess({
        message: "Analytics Dashboard Access granted successfully",
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        "Failed to grant analytics dashboard access. Please try again.";
      handleError({ message: errorMessage });
    } finally {
      setGrantingAccess(false);
    }
  };

  const selectedMerchant = institutionData?.merchants?.find(
    (merchant) => merchant.accountCategoryId === selectedAccountType
  );

  if (error) {
    return (
      <div className="flex flex-col gap-3 mt-4 border border-border-subtle py-4 px-4 rounded-lg bg-bg-base w-full mx-3">
        <span className="text-text-status-red font-medium text-sm">
          Error fetching data. Please try again later.
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full min-h-screen">
      <div className="h-[56px] border-b border-border-subtle bg-bg-page-body flex flex-col justify-center sticky top-0 bg-white z-10 px-4 w-full">
        <div className="flex flex-row items-center gap-3">
          <img
            src={ArrowBackLight}
            alt="Back"
            className="cursor-pointer"
            onClick={() => navigate(-1)}
            height={20}
            width={20}
          />
          <p className="flex text-text-base text-[14px] font-semibold items-center">
            {heading}
          </p>
        </div>
      </div>

      <div className="px-3 py-3 flex flex-col gap-2">
        <LightCustomButton
          text="ADD SERVICE"
          isActive={!!institutionData?.userId}
          onClick={() => setAddServiceOpen(true)}
        />
        <LightCustomButton
          text="GRANT ANALYTICS DASHBOARD ACCESS"
          isActive={!!institutionData?.userId}
          isButtonLoading={grantingAccess}
          onClick={handleGrantAnalyticsDashboardAccess}
        />
      </div>

      <div className="flex flex-col gap-4 px-3 pb-4">
        <div className="flex flex-col gap-3 px-3 py-2 bg-bg-base rounded-lg border border-border-subtle">
          <p className="text-text-base text-[14px] font-semibold mb-2">
            Account Details
          </p>
          <div className="flex flex-row items-center justify-between">
            <p className="text-text-label text-[12px] font-medium">
              First name
            </p>
            <p className="text-text-base text-[12px] font-semibold">
              {institutionData?.firstName}
            </p>
          </div>
          <div className="flex flex-row items-center justify-between">
            <p className="text-text-label text-[12px] font-medium">Last name</p>
            <p className="text-text-base text-[12px] font-semibold">
              {institutionData?.lastName}
            </p>
          </div>
          <div className="flex flex-row items-center justify-between">
            <p className="text-text-label text-[12px] font-medium">
              Business name
            </p>
            <p className="text-text-base text-[12px] font-semibold">
              {institutionData?.legalEntityName}
            </p>
          </div>
          <div className="flex flex-row items-center justify-between">
            <p className="text-text-label text-[12px] font-medium">
              Business URL
            </p>
            <p className="text-text-base text-[12px] font-semibold text-right max-w-[60%] break-all">
              {institutionData?.businessUrl}
            </p>
          </div>
          <div className="flex flex-row items-center justify-between">
            <p className="text-text-label text-[12px] font-medium">Email ID</p>
            <p className="text-text-base text-[12px] font-semibold text-right max-w-[60%] break-all">
              {institutionData?.emailId}
            </p>
          </div>
          <div className="flex flex-row items-center justify-between">
            <p className="text-text-label text-[12px] font-medium">
              WhatsApp number
            </p>
            <p className="text-text-base text-[12px] font-semibold">
              {institutionData?.contactCountryCode}{" "}
              {institutionData?.contactNumber}
            </p>
          </div>
          <div className="flex flex-row items-center justify-between">
            <p className="text-text-label text-[12px] font-medium">
              Referred by
            </p>
            <p className="text-text-base text-[12px] font-semibold">
              {institutionData?.refby || "N/A"}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 px-3 py-2 bg-bg-base rounded-lg border border-border-subtle">
          <p className="text-text-base text-[14px] font-semibold mb-2">
            Merchants Created
          </p>
          <div className="flex flex-col">
            <div className="flex flex-row bg-bg-base px-[8px] py-[8px] rounded-t-[16px] border border-border-subtle">
              <span className="font-semibold text-[10px] w-[45%] flex justify-start">
                Merchant Type
              </span>
              <span className="font-semibold text-[10px] w-[25%] flex justify-start">
                Merchant ID
              </span>
              <span className="font-semibold text-[10px] w-[30%] flex justify-end">
                Status
              </span>
            </div>
            {institutionData?.merchants && (
              <div className="max-h-[300px] overflow-y-auto overflow-x-hidden border-r border-l border-b border-border-subtle rounded-b-[16px] bg-bg-page-body">
                {institutionData?.merchants.map((merchant, index) => (
                  <div
                    key={index}
                    className={`px-[12px] py-[8px] flex flex-row items-center cursor-pointer ${
                      selectedAccountType === merchant.accountCategoryId
                        ? "border-y border-text-navbar-active"
                        : "border-b border-border-subtle"
                    }`}
                    onClick={() =>
                      setSelectedAccountType(merchant.accountCategoryId)
                    }
                  >
                    <span className="text-[8px] w-[45%] text-text-base font-medium flex justify-start">
                      {merchant.accountCategoryName}
                    </span>
                    <span className="text-[8px] w-[25%] text-text-base font-medium flex justify-start">
                      {merchant.merchantId}
                    </span>
                    <div className="flex w-[30%] justify-end items-center">
                        <DataPill
                          value={merchant.active === 0 ? "inactive" : "active"}
                        />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!loading &&
              (!institutionData?.merchants ||
                institutionData.merchants.length === 0) && (
                <div className="border border-t-0 border-border-subtle rounded-b-[16px] px-3 py-4 text-text-label text-[12px]">
                  No merchants yet. Use Add Service to create one.
                </div>
              )}
          </div>
        </div>

        {selectedMerchant ? (
          <div className="w-full [&_>div]:w-full">
            <MerchantConfigPanel
              key={selectedMerchant.merchantId}
              merchantId={selectedMerchant.merchantId}
              userId={Number(institutionData?.userId!)}
              accountCategoryId={selectedMerchant.accountCategoryId}
              accountCategoryName={selectedMerchant.accountCategoryName}
              onConfigChanged={fetchInstitutionData}
            />
          </div>
        ) : (
          <div className="w-full flex items-center justify-center py-8 text-text-label text-[14px] font-medium">
            Select a merchant to view or update config
          </div>
        )}
      </div>

      {institutionData?.userId && (
        <AddInstitutionServiceSlider
          isOpen={addServiceOpen}
          onClose={() => setAddServiceOpen(false)}
          userId={Number(institutionData.userId)}
          existingAccountCategoryIds={
            institutionData.merchants?.map((m) => m.accountCategoryId) || []
          }
          onSuccess={fetchInstitutionData}
        />
      )}
    </div>
  );
};

export default MobileInstitutionalManagementDetails;
